/**
 * hk_os · <PhotoEvidenceUploader>
 * ────────────────────────────────────────────────────────────────────
 * Camera-first photo evidence UI. Multi-shot capable, preview thumbnails,
 * retake + delete per shot, compression indicator, upload progress bar.
 *
 * Consumer owns the actual upload (POST /api/oa/v1/media/upload with
 * kind=photo) via `onUpload({file, index, onProgress})`. This primitive
 * is UI only.
 *
 * Props:
 *   voiceKey       — snippet played once when uploader opens (default VOICE_PHOTO_REQUIRED)
 *   maxPhotos      — cap (default 4)
 *   maxBytes       — per-photo (default 4 MB, browser-side compression target)
 *   onChange       — ({photos: [{blob, mimeType, size, previewUrl, status}]})
 *   onUpload       — async ({file, index}) → returns {ok, media_id, error?}
 *   testid
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Camera, Trash2, RotateCcw, CheckCircle2, Loader2, AlertTriangle,
} from "lucide-react";
import { useVoiceRegistry } from "./useVoiceRegistry";

const UPLOAD_STATES = {
  ready:    { icon: null, cls: "bg-white border-slate-200" },
  uploading:{ icon: Loader2, cls: "bg-sky-50 border-sky-300" },
  done:     { icon: CheckCircle2, cls: "bg-emerald-50 border-emerald-300" },
  error:    { icon: AlertTriangle, cls: "bg-red-50 border-red-300" },
};

async function compressToJpeg(file, maxBytes) {
  if (file.size <= maxBytes) return file;
  try {
    const bitmap = await createImageBitmap(file);
    // Cap the longest edge at 1600 px — good enough for defect evidence.
    const maxEdge = 1600;
    const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    canvas.getContext("2d").drawImage(bitmap, 0, 0, w, h);
    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", 0.82));
    if (blob && blob.size < file.size) {
      return new File([blob], file.name.replace(/\.\w+$/, ".jpg"), {
        type: "image/jpeg",
      });
    }
  } catch { /* fall through */ }
  return file;
}

export function PhotoEvidenceUploader({
  voiceKey = "VOICE_PHOTO_REQUIRED",
  maxPhotos = 4,
  maxBytes = 4 * 1024 * 1024,
  onChange,
  onUpload,
  testid,
}) {
  const { play, getSnippet } = useVoiceRegistry();
  const snippet = getSnippet(voiceKey);
  const [photos, setPhotos] = useState([]);
  const [compressing, setCompressing] = useState(false);
  const inputRef = useRef(null);
  const bootstrapped = useRef(false);

  useEffect(() => {
    if (!bootstrapped.current) {
      bootstrapped.current = true;
      play(voiceKey).catch(() => {});
    }
  }, [play, voiceKey]);

  useEffect(() => { onChange?.({ photos }); }, [photos, onChange]);

  const upload = useCallback(async (photo, index) => {
    if (!onUpload) return;
    setPhotos((all) => all.map((p, i) => i === index ? { ...p, status: "uploading", progress: 0 } : p));
    const res = await onUpload({
      file: photo.blob, index,
      onProgress: (pct) =>
        setPhotos((all) => all.map((p, i) => i === index ? { ...p, progress: pct } : p)),
    }).catch(() => ({ ok: false, error: "network" }));
    setPhotos((all) => all.map((p, i) => i === index
      ? { ...p, status: res?.ok ? "done" : "error", media_id: res?.media_id, error: res?.error }
      : p));
  }, [onUpload]);

  const addFile = useCallback(async (file) => {
    if (!file) return;
    setCompressing(true);
    const compressed = await compressToJpeg(file, maxBytes);
    setCompressing(false);
    const previewUrl = URL.createObjectURL(compressed);
    setPhotos((all) => {
      if (all.length >= maxPhotos) return all;
      const idx = all.length;
      const next = [...all, {
        blob: compressed, mimeType: compressed.type,
        size: compressed.size, previewUrl, status: "ready",
      }];
      // Kick off upload after the render.
      queueMicrotask(() => upload(next[idx], idx));
      return next;
    });
  }, [maxBytes, maxPhotos, upload]);

  const remove = (i) => {
    setPhotos((all) => {
      const target = all[i];
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return all.filter((_, idx) => idx !== i);
    });
  };

  const retake = (i) => {
    remove(i);
    inputRef.current?.click();
  };

  const openCamera = () => inputRef.current?.click();

  return (
    <div
      data-testid={testid || "photo-evidence-uploader"}
      className="space-y-3"
    >
      {snippet?.text && (
        <div className="text-[14px] text-slate-600" lang={snippet.locale || "bn-IN"}>
          {snippet.text}
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        data-testid={testid ? `${testid}-input` : "photo-file-input"}
        onChange={(e) => addFile(e.target.files?.[0])}
      />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {photos.map((p, i) => {
          const st = UPLOAD_STATES[p.status] || UPLOAD_STATES.ready;
          const StatusIcon = st.icon;
          return (
            <div
              key={i}
              data-testid={testid ? `${testid}-photo-${i}` : `photo-${i}`}
              className={`relative aspect-square rounded-2xl border-2 overflow-hidden ${st.cls}`}
            >
              <img src={p.previewUrl} alt={`evidence ${i + 1}`}
                    className="absolute inset-0 w-full h-full object-cover" />
              {/* Progress overlay while uploading */}
              {p.status === "uploading" && (
                <div className="absolute inset-x-0 bottom-0 h-1.5 bg-white/50">
                  <div className="h-full bg-sky-500"
                        style={{ width: `${(p.progress || 0) * 100}%` }} />
                </div>
              )}
              {StatusIcon && (
                <span className="absolute top-1.5 right-1.5 rounded-full bg-white/95 p-1 shadow">
                  <StatusIcon
                    className={`w-4 h-4 ${p.status === "uploading" ? "animate-spin text-sky-600"
                                          : p.status === "done" ? "text-emerald-600"
                                          : "text-red-600"}`}
                    strokeWidth={2.4}
                  />
                </span>
              )}
              <div className="absolute inset-x-0 bottom-0 flex justify-between gap-1 p-1">
                <button type="button"
                  data-testid={`photo-${i}-retake`}
                  aria-label="Retake"
                  onClick={() => retake(i)}
                  className="rounded-full bg-white/95 border border-slate-200 h-8 w-8 flex items-center justify-center shadow">
                  <RotateCcw className="w-4 h-4 text-slate-700" />
                </button>
                <button type="button"
                  data-testid={`photo-${i}-delete`}
                  aria-label="Delete"
                  onClick={() => remove(i)}
                  className="rounded-full bg-white/95 border border-slate-200 h-8 w-8 flex items-center justify-center shadow">
                  <Trash2 className="w-4 h-4 text-red-600" />
                </button>
              </div>
            </div>
          );
        })}
        {photos.length < maxPhotos && (
          <button
            type="button"
            onClick={openCamera}
            disabled={compressing}
            data-testid={testid ? `${testid}-add` : "photo-add"}
            className="aspect-square rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100 flex flex-col items-center justify-center gap-2 transition-colors disabled:opacity-50"
          >
            {compressing ? (
              <Loader2 className="w-8 h-8 text-slate-500 animate-spin" />
            ) : (
              <Camera className="w-8 h-8 text-slate-500" strokeWidth={2.2} />
            )}
            <span className="text-[13px] font-semibold text-slate-600">
              {compressing ? "Compressing…" : "Take photo"}
            </span>
          </button>
        )}
      </div>
    </div>
  );
}

export default PhotoEvidenceUploader;
